import { useState, useCallback, useEffect, useRef } from "react";
import { T, Badge, Card, Btn, SectionTitle, fmtCcy, fmtDateTime, font, mono } from '../theme.js';
import { api } from '../api-client.js';

const FINANCE_NAV = [
  { id: 'dashboard', label: 'Month End', icon: '\u25C9' },
  { id: 'pnl', label: 'Profit & Loss', icon: '\u2261' },
  { id: 'balance-sheet', label: 'Balance Sheet', icon: '\u229F' },
  { id: 'journals', label: 'Journal Search', icon: '\u2263' },
  { id: 'periods', label: 'Periods', icon: '\u29BE' },
  { id: 'trial-balance', label: 'Trial Balance Export', icon: '\u21A7' },
  { id: 'year-end', label: 'Year-End Close', icon: '\u2716' },
];

export default function FinanceModule({ ledger, refresh: parentRefresh }) {
  const [view, setView] = useState('dashboard');
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);

  const refresh = useCallback(() => {
    setTick(t => t + 1);
    if (parentRefresh) parentRefresh();
  }, [parentRefresh]);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  const showError = useCallback(msg => showToast(msg, 'error'), [showToast]);

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000, padding: '10px 16px',
          background: toast.type === 'error' ? T.redBg : T.greenBg,
          border: `1px solid ${toast.type === 'error' ? T.red : T.green}40`,
          borderRadius: 6, fontSize: 12, maxWidth: 420, fontFamily: font }}>
          <span style={{ color: toast.type === 'error' ? T.red : T.green, marginRight: 8 }}>{toast.type === 'error' ? '\u2717' : '\u2713'}</span>{toast.msg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {FINANCE_NAV.map(n => (
          <button key={n.id} onClick={() => setView(n.id)} style={{
            padding: '8px 14px', fontSize: 12, fontWeight: view === n.id ? 600 : 400,
            color: view === n.id ? T.blue : T.textSecondary,
            background: 'transparent', border: 'none',
            borderBottom: view === n.id ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1,
            whiteSpace: 'nowrap',
          }}>
            <span style={{ marginRight: 5 }}>{n.icon}</span>{n.label}
          </button>
        ))}
      </div>

      {view === 'dashboard' && <MonthEndDashboard key={tick} showToast={showToast} showError={showError} />}
      {view === 'pnl' && <ProfitAndLossView key={tick} />}
      {view === 'balance-sheet' && <BalanceSheetView key={tick} />}
      {view === 'journals' && <JournalSearchView key={tick} ledger={ledger} />}
      {view === 'periods' && <PeriodManagementView key={tick} showToast={showToast} showError={showError} refresh={refresh} />}
      {view === 'trial-balance' && <TrialBalanceExportView key={tick} />}
      {view === 'year-end' && <YearEndCloseView key={tick} showToast={showToast} showError={showError} refresh={refresh} />}
    </div>
  );
}

// ============================================================
// MONTH END DASHBOARD
// ============================================================

function MonthEndDashboard({ showToast, showError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const summary = await api.get('/gl/month-end-summary');
      setData(summary);
    } catch (e) { showError(e.message); }
    finally { setLoading(false); }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ color: T.textMuted, fontSize: 13 }}>Loading dashboard...</div>;
  if (!data) return <div style={{ color: T.red, fontSize: 13 }}>Failed to load dashboard</div>;

  const periodLabel = data.period?.status === 'NOT_CREATED' ? 'Not Created' : `${data.period.year}-${String(data.period.month).padStart(2, '0')}`;
  const periodColor = { OPEN: 'green', CLOSED: 'amber', LOCKED: 'blue', NOT_CREATED: 'gray' }[data.period?.status] || 'gray';

  return (
    <div>
      <SectionTitle>Month-End Dashboard</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Card>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Current Period</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{periodLabel}</div>
          <Badge color={periodColor}>{data.period?.status || 'N/A'}</Badge>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Trial Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{data.trialBalance?.accountCount || 0} accounts</div>
          <Badge color={data.trialBalance?.balanced ? 'green' : 'red'}>{data.trialBalance?.balanced ? 'BALANCED' : 'IMBALANCED'}</Badge>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Integrity Check</div>
          <Badge color={data.integrity?.valid ? 'green' : 'red'}>{data.integrity?.valid ? 'VALID' : 'ERROR'}</Badge>
          {data.integrity?.issues?.length > 0 && <div style={{ fontSize: 11, color: T.red, marginTop: 4 }}>{data.integrity.issues.length} issue(s)</div>}
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Net Interest Margin</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{(data.nim * 100).toFixed(2)}%</div>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <Card>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Period P&L</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12 }}>Income</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.green }}>{fmtCcy(data.pnl?.totalIncome || 0)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12 }}>Expenses</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: T.red }}>{fmtCcy(data.pnl?.totalExpenses || 0)}</span>
          </div>
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 4, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Net Income</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: (data.pnl?.netIncome || 0) >= 0 ? T.green : T.red }}>{fmtCcy(data.pnl?.netIncome || 0)}</span>
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Balance Sheet</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12 }}>Total Assets</span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{fmtCcy(data.balanceSheet?.totalAssets || 0)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12 }}>Total Liabilities</span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{fmtCcy(data.balanceSheet?.totalLiabilities || 0)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12 }}>Total Equity</span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{fmtCcy(data.balanceSheet?.totalEquity || 0)}</span>
          </div>
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 4 }}>
            <Badge color={data.balanceSheet?.balanceCheck ? 'green' : 'red'}>{data.balanceSheet?.balanceCheck ? 'A = L + E' : 'IMBALANCED'}</Badge>
          </div>
        </Card>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Btn onClick={load} variant="secondary" small>Refresh</Btn>
      </div>
    </div>
  );
}

// ============================================================
// PROFIT & LOSS VIEW
// ============================================================

function PnlChangeCell({ value, pct, invertColor }) {
  if (value === null || value === undefined) return <span style={{ fontFamily: mono, color: T.textMuted }}>{'\u2014'}</span>;
  const positive = value > 0;
  const color = invertColor ? (positive ? T.red : T.green) : (positive ? T.green : T.red);
  const sign = value > 0 ? '+' : '';
  return (
    <span style={{ fontFamily: mono, color: value === 0 ? T.textMuted : color }}>
      {sign}{fmtCcy(value)}{pct !== null && pct !== undefined ? ` (${sign}${pct.toFixed(2)}%)` : ''}
    </span>
  );
}

function ProfitAndLossView() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('month'); // month | ytd
  const [comparative, setComparative] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint;
      if (mode === 'ytd') {
        endpoint = `/gl/pnl/ytd?year=${year}&month=${month}`;
        if (comparative === 'prior_year') endpoint += `&comparative=prior_year`;
      } else {
        endpoint = `/gl/pnl?year=${year}&month=${month}`;
        if (comparative) endpoint += `&comparative=${comparative}`;
      }
      setData(await api.get(endpoint));
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [year, month, mode, comparative]);

  useEffect(() => { load(); }, [load]);

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const hasComparative = comparative && data && (data.totalIncomePriorPeriod !== undefined || data.totalIncomePriorYear !== undefined);
  const hasPP = hasComparative && data.totalIncomePriorPeriod !== null;
  const hasPY = hasComparative && data.totalIncomePriorYear !== null;

  const comparativeOptions = mode === 'ytd'
    ? [{ value: '', label: 'None' }, { value: 'prior_year', label: 'Prior Year' }]
    : [{ value: '', label: 'None' }, { value: 'prior_period', label: 'Prior Period' }, { value: 'prior_year', label: 'Prior Year' }, { value: 'both', label: 'Both' }];

  return (
    <div>
      <SectionTitle>Profit & Loss</SectionTitle>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={year} onChange={e => setYear(+e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
          {[2024,2025,2026,2027,2028,2029,2030].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={month} onChange={e => setMonth(+e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
          {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <Btn onClick={() => { setMode(mode === 'month' ? 'ytd' : 'month'); setComparative(''); }} variant="secondary" small>{mode === 'month' ? 'Switch to YTD' : 'Switch to Monthly'}</Btn>
        <span style={{ fontSize: 12, color: T.textMuted }}>Compare with:</span>
        <select value={comparative} onChange={e => setComparative(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
          {comparativeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading && <div style={{ color: T.textMuted, fontSize: 13 }}>Loading...</div>}
      {!loading && data && (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: T.green }}>Income</div>
            {data.income.length === 0 && <div style={{ fontSize: 12, color: T.textMuted }}>No income in selected period.</div>}
            {data.income.map(item => (
              <div key={item.accountNumber} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, gap: 12 }}>
                <span style={{ flex: 1 }}>{item.name}</span>
                <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right' }}>{fmtCcy(item.amount)}</span>
                {hasPP && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{item.priorPeriod !== null ? fmtCcy(item.priorPeriod) : '\u2014'}</span>}
                {hasPP && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={item.periodChange} pct={item.periodChangePct} /></span>}
                {hasPY && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{item.priorYear !== null ? fmtCcy(item.priorYear) : '\u2014'}</span>}
                {hasPY && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={item.yearChange} pct={item.yearChangePct} /></span>}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, fontWeight: 600, borderTop: `1px solid ${T.border}`, marginTop: 4, gap: 12 }}>
              <span style={{ flex: 1 }}>Total Income</span>
              <span style={{ fontFamily: mono, color: T.green, minWidth: 90, textAlign: 'right' }}>{fmtCcy(data.totalIncome)}</span>
              {hasPP && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{fmtCcy(data.totalIncomePriorPeriod)}</span>}
              {hasPP && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={Math.round((data.totalIncome - data.totalIncomePriorPeriod) * 100) / 100} pct={data.totalIncomePriorPeriod ? Math.round((data.totalIncome - data.totalIncomePriorPeriod) / data.totalIncomePriorPeriod * 10000) / 100 : null} /></span>}
              {hasPY && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{data.totalIncomePriorYear !== null ? fmtCcy(data.totalIncomePriorYear) : '\u2014'}</span>}
              {hasPY && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={data.totalIncomePriorYear !== null ? Math.round((data.totalIncome - data.totalIncomePriorYear) * 100) / 100 : null} pct={data.totalIncomePriorYear ? Math.round((data.totalIncome - data.totalIncomePriorYear) / data.totalIncomePriorYear * 10000) / 100 : null} /></span>}
            </div>
          </div>

          {hasComparative && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, fontSize: 11, color: T.textMuted, marginBottom: 4, paddingRight: 0 }}>
              <span style={{ minWidth: 90, textAlign: 'right' }}>Current</span>
              {hasPP && <span style={{ minWidth: 90, textAlign: 'right' }}>Prior Period</span>}
              {hasPP && <span style={{ minWidth: 130, textAlign: 'right' }}>Change</span>}
              {hasPY && <span style={{ minWidth: 90, textAlign: 'right' }}>Prior Year</span>}
              {hasPY && <span style={{ minWidth: 130, textAlign: 'right' }}>Change</span>}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: T.red }}>Expenses</div>
            {data.expenses.length === 0 && <div style={{ fontSize: 12, color: T.textMuted }}>No expenses in selected period.</div>}
            {data.expenses.map(item => (
              <div key={item.accountNumber} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, gap: 12 }}>
                <span style={{ flex: 1 }}>{item.name}</span>
                <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right' }}>{fmtCcy(item.amount)}</span>
                {hasPP && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{item.priorPeriod !== null ? fmtCcy(item.priorPeriod) : '\u2014'}</span>}
                {hasPP && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={item.periodChange} pct={item.periodChangePct} invertColor /></span>}
                {hasPY && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{item.priorYear !== null ? fmtCcy(item.priorYear) : '\u2014'}</span>}
                {hasPY && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={item.yearChange} pct={item.yearChangePct} invertColor /></span>}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, fontWeight: 600, borderTop: `1px solid ${T.border}`, marginTop: 4, gap: 12 }}>
              <span style={{ flex: 1 }}>Total Expenses</span>
              <span style={{ fontFamily: mono, color: T.red, minWidth: 90, textAlign: 'right' }}>{fmtCcy(data.totalExpenses)}</span>
              {hasPP && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{fmtCcy(data.totalExpensesPriorPeriod)}</span>}
              {hasPP && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={Math.round((data.totalExpenses - data.totalExpensesPriorPeriod) * 100) / 100} pct={data.totalExpensesPriorPeriod ? Math.round((data.totalExpenses - data.totalExpensesPriorPeriod) / data.totalExpensesPriorPeriod * 10000) / 100 : null} invertColor /></span>}
              {hasPY && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{data.totalExpensesPriorYear !== null ? fmtCcy(data.totalExpensesPriorYear) : '\u2014'}</span>}
              {hasPY && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={data.totalExpensesPriorYear !== null ? Math.round((data.totalExpenses - data.totalExpensesPriorYear) * 100) / 100 : null} pct={data.totalExpensesPriorYear ? Math.round((data.totalExpenses - data.totalExpensesPriorYear) / data.totalExpensesPriorYear * 10000) / 100 : null} invertColor /></span>}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 15, fontWeight: 700, borderTop: `2px solid ${T.text}`, gap: 12 }}>
            <span style={{ flex: 1 }}>Net Income</span>
            <span style={{ fontFamily: mono, color: data.netIncome >= 0 ? T.green : T.red, minWidth: 90, textAlign: 'right' }}>{fmtCcy(data.netIncome)}</span>
            {hasPP && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{data.netIncomePriorPeriod !== null ? fmtCcy(data.netIncomePriorPeriod) : '\u2014'}</span>}
            {hasPP && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={data.netIncomePriorPeriod !== null ? Math.round((data.netIncome - data.netIncomePriorPeriod) * 100) / 100 : null} pct={data.netIncomePriorPeriod ? Math.round((data.netIncome - data.netIncomePriorPeriod) / data.netIncomePriorPeriod * 10000) / 100 : null} /></span>}
            {hasPY && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{data.netIncomePriorYear !== null ? fmtCcy(data.netIncomePriorYear) : '\u2014'}</span>}
            {hasPY && <span style={{ minWidth: 130, textAlign: 'right' }}><PnlChangeCell value={data.netIncomePriorYear !== null ? Math.round((data.netIncome - data.netIncomePriorYear) * 100) / 100 : null} pct={data.netIncomePriorYear ? Math.round((data.netIncome - data.netIncomePriorYear) / data.netIncomePriorYear * 10000) / 100 : null} /></span>}
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// BALANCE SHEET VIEW
// ============================================================

function BsChangeCell({ value, pct }) {
  if (value === null || value === undefined) return <span style={{ fontFamily: mono, color: T.textMuted }}>{'\u2014'}</span>;
  const sign = value > 0 ? '+' : '';
  const color = value > 0 ? T.green : value < 0 ? T.red : T.textMuted;
  return (
    <span style={{ fontFamily: mono, color }}>
      {sign}{fmtCcy(value)}{pct !== null && pct !== undefined ? ` (${sign}${pct.toFixed(2)}%)` : ''}
    </span>
  );
}

function BalanceSheetView() {
  const [asOf, setAsOf] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparative, setComparative] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = `/gl/balance-sheet?asOf=${asOf}`;
      if (comparative) endpoint += `&comparative=${comparative}`;
      setData(await api.get(endpoint));
    }
    catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [asOf, comparative]);

  useEffect(() => { load(); }, [load]);

  const hasComp = comparative && data && data.totalAssetsPrior !== undefined;

  const Section = ({ title, items, total, totalPrior, color }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color }}>{title}</div>
      {items.map(item => (
        <div key={item.accountNumber} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, gap: 12 }}>
          <span style={{ flex: 1 }}>{item.name}</span>
          <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right' }}>{fmtCcy(item.balance, item.currency)}</span>
          {hasComp && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{item.priorBalance !== null ? fmtCcy(item.priorBalance, item.currency) : '\u2014'}</span>}
          {hasComp && <span style={{ minWidth: 140, textAlign: 'right' }}><BsChangeCell value={item.change} pct={item.changePct} /></span>}
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, fontWeight: 600, borderTop: `1px solid ${T.border}`, marginTop: 4, gap: 12 }}>
        <span style={{ flex: 1 }}>Total {title}</span>
        <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right' }}>{fmtCcy(total)}</span>
        {hasComp && <span style={{ fontFamily: mono, minWidth: 90, textAlign: 'right', color: T.textMuted }}>{totalPrior !== null ? fmtCcy(totalPrior) : '\u2014'}</span>}
        {hasComp && <span style={{ minWidth: 140, textAlign: 'right' }}><BsChangeCell value={totalPrior !== null ? Math.round((total - totalPrior) * 100) / 100 : null} pct={totalPrior ? Math.round((total - totalPrior) / totalPrior * 10000) / 100 : null} /></span>}
      </div>
    </div>
  );

  return (
    <div>
      <SectionTitle>Balance Sheet</SectionTitle>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }} />
        <span style={{ fontSize: 12, color: T.textMuted }}>Compare with:</span>
        <select value={comparative} onChange={e => setComparative(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
          <option value="">None</option>
          <option value="prior_month">Prior Month</option>
          <option value="prior_year">Prior Year</option>
        </select>
      </div>

      {loading && <div style={{ color: T.textMuted, fontSize: 13 }}>Loading...</div>}
      {!loading && data && (
        <Card>
          {hasComp && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
              <span style={{ minWidth: 90, textAlign: 'right' }}>Current</span>
              <span style={{ minWidth: 90, textAlign: 'right' }}>Prior</span>
              <span style={{ minWidth: 140, textAlign: 'right' }}>Change</span>
            </div>
          )}
          <Section title="Assets" items={data.assets} total={data.totalAssets} totalPrior={hasComp ? data.totalAssetsPrior : null} color={T.blue} />
          <Section title="Liabilities" items={data.liabilities} total={data.totalLiabilities} totalPrior={hasComp ? data.totalLiabilitiesPrior : null} color={T.amber} />
          <Section title="Equity" items={data.equity} total={data.totalEquity} totalPrior={hasComp ? data.totalEquityPrior : null} color={T.purple} />

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 13, fontWeight: 700, borderTop: `2px solid ${T.text}` }}>
            <span>Balance Check (A = L + E)</span>
            <Badge color={data.balanceCheck ? 'green' : 'red'}>{data.balanceCheck ? 'BALANCED' : 'IMBALANCED'}</Badge>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// JOURNAL SEARCH VIEW
// ============================================================

function JournalSearchView({ ledger }) {
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', accountId: '', type: '', minAmount: '', maxAmount: '', reference: '', createdBy: '', page: 1, pageSize: 50 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const accs = await api.get('/ledger/accounts');
        setAccounts(accs);
      } catch (_) {}
    })();
  }, []);

  const search = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.accountId) params.set('accountId', filters.accountId);
      if (filters.type) params.set('type', filters.type);
      if (filters.minAmount) params.set('minAmount', filters.minAmount);
      if (filters.maxAmount) params.set('maxAmount', filters.maxAmount);
      if (filters.reference) params.set('reference', filters.reference);
      if (filters.createdBy) params.set('createdBy', filters.createdBy);
      params.set('page', page);
      params.set('pageSize', filters.pageSize);
      setData(await api.get(`/gl/journals?${params.toString()}`));
      setFilters(f => ({ ...f, page }));
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.accountId) params.set('accountId', filters.accountId);
    if (filters.type) params.set('type', filters.type);
    if (filters.reference) params.set('reference', filters.reference);

    const { getToken } = await import('../auth.js');
    const token = getToken();
    const base = window.location.hostname === 'localhost' ? 'http://localhost:3010/api' : '/api';
    const res = await fetch(`${base}/gl/journals/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `journals-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filters]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  return (
    <div>
      <SectionTitle>Journal Search</SectionTitle>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Date From</label>
            <input type="date" value={filters.dateFrom} onChange={e => setFilter('dateFrom', e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Date To</label>
            <input type="date" value={filters.dateTo} onChange={e => setFilter('dateTo', e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Account</label>
            <select value={filters.accountId} onChange={e => setFilter('accountId', e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }}>
              <option value="">All accounts</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.accountNumber} - {a.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Type</label>
            <input value={filters.type} onChange={e => setFilter('type', e.target.value)} placeholder="e.g. DEPOSIT" style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Min Amount</label>
            <input type="number" value={filters.minAmount} onChange={e => setFilter('minAmount', e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Max Amount</label>
            <input type="number" value={filters.maxAmount} onChange={e => setFilter('maxAmount', e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Reference</label>
            <input value={filters.reference} onChange={e => setFilter('reference', e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textMuted, display: 'block', marginBottom: 2 }}>Posted By</label>
            <input value={filters.createdBy} onChange={e => setFilter('createdBy', e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={() => search(1)} small>Search</Btn>
          <Btn onClick={exportCsv} variant="secondary" small>Export CSV</Btn>
        </div>
      </Card>

      {loading && <div style={{ color: T.textMuted, fontSize: 13 }}>Searching...</div>}
      {!loading && data && (
        <>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>{data.pagination.total} entries found (page {data.pagination.page} of {data.pagination.totalPages})</div>
          <Card style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Reference</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Account</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Debit</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Credit</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Posted By</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                    <td style={{ padding: '6px 8px', fontFamily: mono, fontSize: 11 }}>{fmtDateTime(e.created_at)}</td>
                    <td style={{ padding: '6px 8px' }}>{e.reference || '\u2014'}</td>
                    <td style={{ padding: '6px 8px' }}><Badge color="gray">{e.transaction_type}</Badge></td>
                    <td style={{ padding: '6px 8px', fontSize: 11 }}>{e.account_number}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: mono }}>{e.direction === 'DEBIT' ? fmtCcy(parseFloat(e.amount), e.currency) : ''}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: mono }}>{e.direction === 'CREDIT' ? fmtCcy(parseFloat(e.amount), e.currency) : ''}</td>
                    <td style={{ padding: '6px 8px', fontSize: 11 }}>{e.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          {data.pagination.totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
              <Btn onClick={() => search(filters.page - 1)} variant="secondary" small disabled={filters.page <= 1}>Prev</Btn>
              <span style={{ fontSize: 12, color: T.textMuted, lineHeight: '28px' }}>Page {filters.page} / {data.pagination.totalPages}</span>
              <Btn onClick={() => search(filters.page + 1)} variant="secondary" small disabled={filters.page >= data.pagination.totalPages}>Next</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// PERIOD MANAGEMENT VIEW
// ============================================================

function PeriodManagementView({ showToast, showError, refresh }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [newYear, setNewYear] = useState(new Date().getFullYear());
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setPeriods(await api.get('/gl/periods'));
    } catch (e) { showError(e.message); }
    finally { setLoading(false); }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const openPeriod = async () => {
    try {
      setActionLoading('open');
      await api.post('/gl/periods', { year: newYear, month: newMonth });
      showToast(`Period ${newYear}-${String(newMonth).padStart(2, '0')} opened`);
      load();
    } catch (e) { showError(e.message); }
    finally { setActionLoading(null); }
  };

  const closePeriod = async (year, month) => {
    const notes = window.prompt('Close notes (optional):');
    if (notes === null) return;
    try {
      setActionLoading(`close-${year}-${month}`);
      await api.post(`/gl/periods/${year}/${month}/close`, { notes: notes || undefined });
      showToast(`Period ${year}-${String(month).padStart(2, '0')} closed`);
      load();
      refresh();
    } catch (e) { showError(e.message); }
    finally { setActionLoading(null); }
  };

  const lockPeriod = async (year, month) => {
    if (!window.confirm(`Lock period ${year}-${String(month).padStart(2, '0')}? This cannot be undone.`)) return;
    try {
      setActionLoading(`lock-${year}-${month}`);
      await api.post(`/gl/periods/${year}/${month}/lock`);
      showToast(`Period ${year}-${String(month).padStart(2, '0')} locked`);
      load();
    } catch (e) { showError(e.message); }
    finally { setActionLoading(null); }
  };

  const reopenPeriod = async (year, month) => {
    try {
      setActionLoading(`reopen-${year}-${month}`);
      await api.post(`/gl/periods/${year}/${month}/reopen`);
      showToast(`Period ${year}-${String(month).padStart(2, '0')} reopened`);
      load();
    } catch (e) { showError(e.message); }
    finally { setActionLoading(null); }
  };

  const statusColor = { OPEN: 'green', CLOSED: 'amber', LOCKED: 'blue' };
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div>
      <SectionTitle>Accounting Periods</SectionTitle>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Open New Period</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={newYear} onChange={e => setNewYear(+e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
            {[2024,2025,2026,2027,2028,2029,2030].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={newMonth} onChange={e => setNewMonth(+e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
            {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <Btn onClick={openPeriod} small disabled={actionLoading === 'open'}>Open Period</Btn>
        </div>
      </Card>

      {loading && <div style={{ color: T.textMuted, fontSize: 13 }}>Loading...</div>}
      {!loading && (
        <Card style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Period</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Opened</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Closed</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Closed By</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map(p => (
                <tr key={`${p.year}-${p.month}`} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>{p.year}-{String(p.month).padStart(2, '0')}</td>
                  <td style={{ padding: '6px 8px' }}><Badge color={statusColor[p.status] || 'gray'}>{p.status}</Badge></td>
                  <td style={{ padding: '6px 8px', fontSize: 11 }}>{fmtDateTime(p.opened_at)}</td>
                  <td style={{ padding: '6px 8px', fontSize: 11 }}>{p.closed_at ? fmtDateTime(p.closed_at) : '\u2014'}</td>
                  <td style={{ padding: '6px 8px', fontSize: 11 }}>{p.closed_by || '\u2014'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {p.status === 'OPEN' && <Btn onClick={() => closePeriod(p.year, p.month)} small variant="secondary" disabled={!!actionLoading}>Close</Btn>}
                      {p.status === 'CLOSED' && <Btn onClick={() => lockPeriod(p.year, p.month)} small disabled={!!actionLoading}>Lock</Btn>}
                      {p.status === 'CLOSED' && <Btn onClick={() => reopenPeriod(p.year, p.month)} small variant="secondary" disabled={!!actionLoading}>Reopen</Btn>}
                    </div>
                  </td>
                </tr>
              ))}
              {periods.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No accounting periods found</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// TRIAL BALANCE EXPORT VIEW
// ============================================================

function TrialBalanceExportView() {
  const [tb, setTb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/ledger/trial-balance');
        setTb(data);
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, []);

  const exportCsv = async () => {
    const { getToken } = await import('../auth.js');
    const token = getToken();
    const base = window.location.hostname === 'localhost' ? 'http://localhost:3010/api' : '/api';
    const res = await fetch(`${base}/gl/trial-balance/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trial-balance-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ color: T.textMuted, fontSize: 13 }}>Loading...</div>;

  const totalDebits = (tb || []).reduce((s, r) => s + ((r.debitBalance || 0)), 0);
  const totalCredits = (tb || []).reduce((s, r) => s + ((r.creditBalance || 0)), 0);
  const balanced = Math.abs(totalDebits - totalCredits) < 0.01;

  return (
    <div>
      <SectionTitle>Trial Balance</SectionTitle>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Btn onClick={exportCsv} small>Export CSV</Btn>
        <Badge color={balanced ? 'green' : 'red'}>{balanced ? 'BALANCED' : 'IMBALANCED'}</Badge>
      </div>

      <Card style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Account</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Name</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Type</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Debit</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {(tb || []).map(row => {
              const debit = row.debitBalance || 0;
              const credit = row.creditBalance || 0;
              if (debit === 0 && credit === 0) return null;
              return (
                <tr key={row.accountNumber} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <td style={{ padding: '6px 8px', fontFamily: mono, fontSize: 11 }}>{row.accountNumber}</td>
                  <td style={{ padding: '6px 8px' }}>{row.name}</td>
                  <td style={{ padding: '6px 8px' }}><Badge color="gray">{row.type}</Badge></td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: mono }}>{debit > 0 ? fmtCcy(debit, row.currency) : ''}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: mono }}>{credit > 0 ? fmtCcy(credit, row.currency) : ''}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${T.text}`, fontWeight: 700 }}>
              <td colSpan={3} style={{ padding: '8px' }}>Totals</td>
              <td style={{ padding: '8px', textAlign: 'right', fontFamily: mono }}>{fmtCcy(totalDebits)}</td>
              <td style={{ padding: '8px', textAlign: 'right', fontFamily: mono }}>{fmtCcy(totalCredits)}</td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}

// ============================================================
// YEAR-END CLOSE VIEW
// ============================================================

function YearEndCloseView({ showToast, showError, refresh }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [periods, setPeriods] = useState([]);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadPeriods = useCallback(async () => {
    try {
      const all = await api.get('/gl/periods');
      setPeriods(all.filter(p => p.year === year));
    } catch (e) { showError(e.message); }
  }, [year, showError]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const yearPeriods = periods.filter(p => p.year === year);
  const allClosed = yearPeriods.length === 12 && yearPeriods.every(p => p.status === 'CLOSED');
  const allLocked = yearPeriods.length === 12 && yearPeriods.every(p => p.status === 'LOCKED');

  const runPreview = async () => {
    try {
      setLoading(true);
      setPreview(null);
      setResult(null);
      const data = await api.post('/gl/year-end-close?dryRun=true', { year });
      setPreview(data);
    } catch (e) { showError(e.message); }
    finally { setLoading(false); }
  };

  const executeClose = async () => {
    try {
      setExecuting(true);
      setConfirmOpen(false);
      const data = await api.post('/gl/year-end-close', { year });
      setResult(data);
      setPreview(null);
      showToast(`Year-end close completed for ${year}`);
      loadPeriods();
      if (refresh) refresh();
    } catch (e) { showError(e.message); }
    finally { setExecuting(false); }
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div>
      <SectionTitle>Year-End Close</SectionTitle>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: T.textMuted }}>Year:</label>
          <select value={year} onChange={e => { setYear(parseInt(e.target.value, 10)); setPreview(null); setResult(null); }}
            style={{ padding: '6px 10px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
            {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Btn onClick={runPreview} small disabled={loading}>
            {loading ? 'Loading...' : 'Preview Close'}
          </Btn>
          {allClosed && !allLocked && preview && !result && (
            <Btn onClick={() => setConfirmOpen(true)} small disabled={executing}>
              {executing ? 'Executing...' : 'Execute Year-End Close'}
            </Btn>
          )}
        </div>

        {allLocked && (
          <div style={{ padding: '10px 14px', background: T.blueBg, border: `1px solid ${T.blue}40`, borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
            All periods for {year} are LOCKED. Year-end close has already been completed.
          </div>
        )}
      </Card>

      {/* Period Status Checklist */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: T.text }}>Period Status Checklist</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 6 }}>
          {monthNames.map((name, i) => {
            const p = yearPeriods.find(yp => yp.month === i + 1);
            const status = p ? p.status : 'MISSING';
            const color = { OPEN: 'amber', CLOSED: 'green', LOCKED: 'blue', MISSING: 'red' }[status];
            return (
              <div key={i} style={{ padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 11, textAlign: 'center' }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{name}</div>
                <Badge color={color}>{status}</Badge>
              </div>
            );
          })}
        </div>
        {!allClosed && !allLocked && (
          <div style={{ marginTop: 10, fontSize: 11, color: T.amber }}>
            All 12 periods must be CLOSED before running year-end close.
          </div>
        )}
      </Card>

      {/* Preview / Dry Run Results */}
      {preview && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: T.text }}>Preview: Closing Journals</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Account</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Direction</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(preview.closingJournals || []).map((j, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <td style={{ padding: '6px 8px', fontFamily: mono, fontSize: 11 }}>{j.accountNumber}</td>
                  <td style={{ padding: '6px 8px' }}><Badge color={j.direction === 'DEBIT' ? 'amber' : 'blue'}>{j.direction}</Badge></td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: mono }}>{fmtCcy(j.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            <div style={{ padding: '8px 12px', background: T.bg, borderRadius: 4, fontSize: 12 }}>
              <span style={{ color: T.textMuted }}>Net P&L: </span>
              <span style={{ fontWeight: 700, fontFamily: mono, color: preview.netPnl >= 0 ? T.green : T.red }}>
                {fmtCcy(preview.netPnl)}
              </span>
            </div>
            <div style={{ padding: '8px 12px', background: T.bg, borderRadius: 4, fontSize: 12 }}>
              <span style={{ color: T.textMuted }}>Retained Earnings (after): </span>
              <span style={{ fontWeight: 700, fontFamily: mono }}>{fmtCcy(preview.retainedEarningsBalance)}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Post-Close Summary */}
      {result && (
        <Card style={{ marginBottom: 16, border: `1px solid ${T.green}40` }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: T.green }}>Year-End Close Complete</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
            <div style={{ padding: '8px 12px', background: T.bg, borderRadius: 4, fontSize: 12 }}>
              <span style={{ color: T.textMuted }}>Net P&L: </span>
              <span style={{ fontWeight: 700, fontFamily: mono, color: result.netPnl >= 0 ? T.green : T.red }}>
                {fmtCcy(result.netPnl)}
              </span>
            </div>
            <div style={{ padding: '8px 12px', background: T.bg, borderRadius: 4, fontSize: 12 }}>
              <span style={{ color: T.textMuted }}>Retained Earnings: </span>
              <span style={{ fontWeight: 700, fontFamily: mono }}>{fmtCcy(result.retainedEarningsBalance)}</span>
            </div>
            <div style={{ padding: '8px 12px', background: T.bg, borderRadius: 4, fontSize: 12 }}>
              <span style={{ color: T.textMuted }}>Periods Locked: </span>
              <span style={{ fontWeight: 700 }}>{result.periodsLocked}</span>
            </div>
            <div style={{ padding: '8px 12px', background: T.bg, borderRadius: 4, fontSize: 12 }}>
              <span style={{ color: T.textMuted }}>Integrity: </span>
              <Badge color={result.integrityCheck?.valid ? 'green' : 'red'}>
                {result.integrityCheck?.valid ? 'VALID' : 'ERROR'}
              </Badge>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Account</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Direction</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: T.textMuted, fontWeight: 500 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(result.closingJournals || []).map((j, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <td style={{ padding: '6px 8px', fontFamily: mono, fontSize: 11 }}>{j.accountNumber}</td>
                  <td style={{ padding: '6px 8px' }}><Badge color={j.direction === 'DEBIT' ? 'amber' : 'blue'}>{j.direction}</Badge></td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: mono }}>{fmtCcy(j.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Confirmation Dialog */}
      {confirmOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <Card style={{ maxWidth: 420, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: T.red }}>Confirm Year-End Close</div>
            <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5, marginBottom: 16 }}>
              This will permanently close the books for {year}. All income and expense accounts will be
              zeroed and their balances swept to Retained Earnings. All 12 periods will be locked.
              This action cannot be reversed.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn onClick={() => setConfirmOpen(false)} variant="secondary" small>Cancel</Btn>
              <Btn onClick={executeClose} small>Confirm Close</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
